import { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";
import axios from "axios";

import AddInvestigatorModal from "../../components/PopUp/AddInvestigatorModal";
import NavBar from "../../components/navBar/navBar";
import AddStaff from "./AddStaff";
import EditStaff from "./EditStaff";
import { LoaderContext } from "../../context/LoaderContext";
import Loader from "../../components/Loader";
import { __API_URL__ } from "../../SERVER_URL";
import useUser from "../../hooks/user";
import { useQueryClient } from "@tanstack/react-query";
import { USER_ROLES } from "../../utils/constants";

import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PersonIcon from "@mui/icons-material/Person";

const MENU_ITEMS = [
  {
    key: "owners",
    staffType: "owner",
    label: "Clinic Owners",
    url: "/api/allclinicownerslist",
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: "admins",
    staffType: "admin",
    label: "Clinic Admins",
    url: "/api/allclinicAdminslist",
    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.CLINIC_OWNER],
  },
  {
    key: "practitioners",
    staffType: "practitioner",
    label: "Practitioners",
    url: "/api/practitioners/practitioner_list",
    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.CLINIC_ADMIN],
  },
  {
    key: "cro_admin",
    staffType: "cro_admin",
    label: "CRO Admin",
    url: "/api/get_croList",
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: "Data Manager",
    staffType: "Data Manager",
    label: "Data Manager",
    url: "/api/patient/patient/get_coordinators",
    roles: [USER_ROLES.CRO, USER_ROLES.SUPER_ADMIN],
  },
  {
    key: "Cro_coordinator",
    staffType: "Cro_coordinator",
    label: "CRO Coordinator",
    url: "/api/patient/patient/get_coordinators",
    roles: [USER_ROLES.CRO, USER_ROLES.SUPER_ADMIN],
  },
  {
    key: "investigator",
    staffType: "investigator",
    label: "Investigators",
    url: "/api/get_investigators",
    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.CRO],
  },
  {
    key: "Inv_coordinator",
    staffType: "Inv_coordinator",
    label: "Inv. Coordinator",
    url: "/api/patient/patient/get_coordinators",
    roles: [USER_ROLES.INVESTIGATOR, USER_ROLES.SUPER_ADMIN, USER_ROLES.CRO],
  },
  {
    key: "Reviewer",
    staffType: "Reviewer",
    label: "Reviewer",
    url: "/api/patient/patient/get_reviewers",
    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.CRO],
  },
  {
    key: "Sub_investigator",
    staffType: "Sub_investigator",
    label: "Sub Investigator",
    url: "/api/patient/patient/get_coordinators",
    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.INVESTIGATOR, USER_ROLES.CRO],
  },
];

const STAFF_TYPE_LABELS = Object.fromEntries(MENU_ITEMS.map((m) => [m.staffType, m.label]));

const TRIAL_RELATED_TYPES = new Set([
  "cro_admin",
  "investigator",
  "Cro_coordinator",
  "Data Manager",
  "Inv_coordinator",
  "Reviewer",
  "Sub_investigator",
]);

const HEAD_USER_TYPES = new Set([
  "Cro_coordinator",
  "Data Manager",
  "Inv_coordinator",
  "Sub_investigator",
]);

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  whiteSpace: "nowrap",
};

const StaffList = () => {
  const user = useUser();
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const loaderContext = useContext(LoaderContext);
  const queryClient = useQueryClient();

  const [hasSearch, setHasSearch] = useState(false);
  const [investigatorModal, setInvestigatorModal] = useState(false);
  const [typeOfStaff, setTypeOfStaff] = useState("owner");
  const [paginationurl, setPaginationurl] = useState("");
  const [patientData, setPatientData] = useState("");
  const [addStaff, setAddStaff] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);
  const [editStaff, setEditStaff] = useState("");
  const [trialsToEdit, setTrialsToEdit] = useState([]);
  const [pageItemCount] = useState(20);
  const [Search, setSearch] = useState({
    email: "",
    head_user_email: "",
    Name: "",
    fName: "",
    lName: "",
    clinic: "",
    fired: "FIRED",
    vip: "VIP",
    do_not_call: "",
    deactive: 0,
    archived: 0,
    itemCount: 20,
    page: 1,
  });

  const resetFilters = () => {
    setSearch({
      itemCount: 20,
      Name: "",
      fName: "",
      lName: "",
      clinic: "",
      email: "",
      head_user_email: "",
      page: 1,
    });
  };

  const handleClick = () => {
    if (typeOfStaff === "investigator") {
      setInvestigatorModal(true);
    } else {
      setAddStaff(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
    setHasSearch(true);
  };

  const PaginationData = () => {
    let coordinator;
    if (typeOfStaff === "Inv_coordinator") coordinator = "Inv_coordinator";
    else if (typeOfStaff === "Cro_coordinator") coordinator = "Cro_coordinator";
    else if (typeOfStaff === "Data Manager") coordinator = "Data Manager";
    else if (typeOfStaff === "Reviewer") coordinator = "Reviewer";
    else if (typeOfStaff === "Sub_investigator") coordinator = "Sub_investigator";

    const emailKeywords = encodeURIComponent(Search.email);
    const headUserEmailKeywords = encodeURIComponent(Search.head_user_email);
    const params = `?page=${Search.page}&page_size=${Search.itemCount}&name=${Search.Name}&first_name=${Search.fName}&last_name=${Search.lName}&clinic=${Search.clinic || ""}&email=${emailKeywords}&head_user=${headUserEmailKeywords}&coordinator=${coordinator}`;

    axios
      .get(`${__API_URL__}${paginationurl}/${params}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => {
        setPatientData(res.data);
        setHasSearch(false);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (hasSearch) {
      setSearch((prev) => ({ ...prev, page: 1 }));
    }
  }, [hasSearch]);

  const UpdateInstance = (e) => {
    e.preventDefault();
    let url;
    if (typeOfStaff === "owner") url = "clinicOwnerGet";
    else if (typeOfStaff === "admin") url = "clinicAdminView";
    else if (
      typeOfStaff === "cro_admin" ||
      typeOfStaff === "investigator" ||
      typeOfStaff === "Cro_coordinator" ||
      typeOfStaff === "Data Manager" ||
      typeOfStaff === "Inv_coordinator" ||
      typeOfStaff === "Sub_investigator"
    )
      url = "edit_crc";
    else if (typeOfStaff === "practitioner") url = "practitioners/practitioner";
    else if (typeOfStaff === "Reviewer") url = "edit_reviewer";

    const CreateData = {
      email: editStaff?.email,
      first_name: editStaff?.first_name,
      last_name: editStaff?.last_name,
      isAdmin: editStaff?.isAdmin,
      isPractitioner: editStaff?.isPractitioner,
      clinic_id: editStaff?.clinic?.["value"],
      name: editStaff?.name,
      trials: trialsToEdit.map((trial) => trial.value),
    };

    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .patch(`${__API_URL__}/api/${url}/${editStaff?.id}/`, CreateData, {
        headers: { Authorization: `Token ${token}` },
      })
      .then(() => {
        toast.success("Staff Updated successfully");
        setEditStaff(false);
        PaginationData(paginationurl);
        queryClient.invalidateQueries({ queryKey: ["trial_logs"] });
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
        toast.error(err.response.data.message);
      })
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const handlePageClick = (event) => {
    const no = event?.selected + 1;
    setSelectedPage(event.selected);
    setSearch((prev) => ({ ...prev, page: no }));
  };

  const handleStaffList = (key) => {
    const item = MENU_ITEMS.find((m) => m.key === key);
    if (!item) return;
    setAddStaff(false);
    setSearch((prev) => ({ ...prev, page: 1 }));
    setPaginationurl(item.url);
    setTypeOfStaff(item.staffType);
  };

  useEffect(() => {
    if (user_data.role === USER_ROLES.CLINIC_OWNER) handleStaffList("admins");
    if (user_data.role === USER_ROLES.CLINIC_ADMIN) handleStaffList("practitioners");
    if (user_data.role === USER_ROLES.CRO) handleStaffList("Cro_coordinator");
    if (user_data.role === USER_ROLES.CRO) handleStaffList("Data Manager");
    if (user_data.role === "Coordinator") handleStaffList("coordinator");
    if (user_data.role === USER_ROLES.INVESTIGATOR) handleStaffList("Inv_coordinator");
    if (user_data.role === USER_ROLES.SUPER_ADMIN) handleStaffList("owners");
    if (user_data.role === USER_ROLES.INVESTIGATOR) handleStaffList("Sub_investigator");
  }, []);

  useEffect(() => {
    if (paginationurl) PaginationData();
  }, [paginationurl, Search]);

  useEffect(() => {
    handlePageClick({ selected: 0 });
  }, [typeOfStaff]);

  const visibleMenuItems = MENU_ITEMS.filter((item) => item.roles.includes(user_data.role));

  const hasTrialColumns = TRIAL_RELATED_TYPES.has(typeOfStaff);
  const showHeadUserCol =
    user_data.role === USER_ROLES.SUPER_ADMIN && HEAD_USER_TYPES.has(typeOfStaff);

  const total = patientData?.count ?? 0;
  const shown = patientData?.results?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageItemCount));
  const staffLabel = STAFF_TYPE_LABELS[typeOfStaff] || typeOfStaff;
  const addLabel = `Add ${staffLabel}`;

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="staff" setAddStaff={setAddStaff} />

      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          overflow: "hidden",
        }}
      >
        {/* ---- LEFT SIDEBAR: Staff Menu ---- */}
        <Paper
          elevation={0}
          sx={{
            width: 240,
            minWidth: 240,
            borderRight: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Staff Menu
            </Typography>
          </Box>
          <Divider />
          <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
            {visibleMenuItems.map((item) => {
              const isActive = typeOfStaff === item.staffType;
              return (
                <ListItemButton
                  key={item.key}
                  selected={isActive}
                  data-testid={`staff-menu-${item.key}`}
                  onClick={() => handleStaffList(item.key)}
                  sx={{
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&.Mui-selected": {
                      bgcolor: "primary.50",
                      borderLeft: "3px solid",
                      borderLeftColor: "primary.main",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: isActive ? "primary.main" : "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <PersonIcon
                      fontSize="small"
                      sx={{ color: isActive ? "white" : "text.secondary" }}
                    />
                  </Box>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: isActive ? 700 : 500,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

        {/* ---- RIGHT PANEL ---- */}
        <Box sx={{ flex: 1, overflow: "auto", bgcolor: "grey.50" }}>
          {addStaff ? (
            <AddStaff
              setAddStaff={setAddStaff}
              toast={toast}
              typeOfStaff={typeOfStaff}
              PaginationData={PaginationData}
              paginationurl={paginationurl}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {staffLabel}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterAltOffIcon />}
                    onClick={resetFilters}
                  >
                    Reset Filters
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleClick}
                  >
                    {addLabel}
                  </Button>
                </Stack>
              </Box>

              {/* Table */}
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 2, maxHeight: "calc(100vh - 250px)" }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {typeOfStaff === "practitioner" && (
                        <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>
                          <TextField
                            size="small"
                            placeholder="Name"
                            name="Name"
                            value={Search.Name}
                            onChange={handleChange}
                            variant="standard"
                            fullWidth
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>
                        <TextField
                          size="small"
                          placeholder="First Name"
                          name="fName"
                          value={Search.fName}
                          onChange={handleChange}
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>
                        <TextField
                          size="small"
                          placeholder="Last Name"
                          name="lName"
                          value={Search.lName}
                          onChange={handleChange}
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ ...headerCellSx, minWidth: 180 }}>
                        <TextField
                          size="small"
                          placeholder="Email"
                          name="email"
                          value={Search.email}
                          onChange={handleChange}
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      {showHeadUserCol && (
                        <TableCell sx={{ ...headerCellSx, minWidth: 180 }}>
                          <TextField
                            size="small"
                            placeholder="Head User Email"
                            name="head_user_email"
                            value={Search.head_user_email}
                            onChange={handleChange}
                            variant="standard"
                            fullWidth
                          />
                        </TableCell>
                      )}
                      {typeOfStaff === "owner" && <TableCell sx={headerCellSx}>Clinics</TableCell>}
                      {(typeOfStaff === "admin" || typeOfStaff === "practitioner") && (
                        <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>
                          <TextField
                            size="small"
                            placeholder="Clinic"
                            name="clinic"
                            value={Search.clinic}
                            onChange={handleChange}
                            variant="standard"
                            fullWidth
                          />
                        </TableCell>
                      )}
                      {hasTrialColumns && (
                        <>
                          <TableCell sx={headerCellSx}>Active Trials</TableCell>
                          <TableCell sx={headerCellSx}>Inactive Trials</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loaderContext.loaderCount > 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : patientData?.results?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>
                            No staff found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      patientData?.results?.map((data, index) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{ cursor: "pointer" }}
                          data-testid="staff-row"
                          data-staff-email={data?.email}
                          onClick={() => {
                            setEditStaff({
                              ...data,
                              clinic:
                                typeOfStaff === "practitioner"
                                  ? {
                                      value: data?.practitioner_clinic?.[0]?.id,
                                      label: data?.practitioner_clinic?.[0]?.name,
                                    }
                                  : {
                                      value: data?.clinics?.[0]?.id,
                                      label: data?.clinics?.[0]?.name,
                                    },
                            });
                          }}
                        >
                          {typeOfStaff === "practitioner" && (
                            <TableCell sx={bodyCellSx}>{data.name}</TableCell>
                          )}
                          <TableCell sx={bodyCellSx}>{data.first_name}</TableCell>
                          <TableCell sx={bodyCellSx}>{data.last_name}</TableCell>
                          <TableCell sx={bodyCellSx}>{data?.email}</TableCell>
                          {showHeadUserCol && (
                            <TableCell sx={bodyCellSx}>{data?.head_user_email}</TableCell>
                          )}
                          {typeOfStaff === "owner" && (
                            <TableCell sx={bodyCellSx}>{data.clinic?.join(", ")}</TableCell>
                          )}
                          {typeOfStaff === "admin" && (
                            <TableCell sx={bodyCellSx}>
                              {data.clinics?.map((c) => c.name).join(", ")}
                            </TableCell>
                          )}
                          {typeOfStaff === "practitioner" &&
                            data?.practitioner_clinic?.[0]?.name && (
                              <TableCell sx={bodyCellSx}>
                                {data.practitioner_clinic[0].name}
                              </TableCell>
                            )}
                          {hasTrialColumns && (
                            <>
                              <TableCell sx={bodyCellSx}>
                                {data?.active_trials?.map((t) => t.label).join(", ")}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                {data?.inactive_trials?.map((t) => t.label).join(", ")}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {shown} out of {total}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={(selectedPage ?? 0) + 1}
                  onChange={(_, page) => handlePageClick({ selected: page - 1 })}
                  siblingCount={2}
                  boundaryCount={1}
                  showFirstButton
                  showLastButton
                  shape="rounded"
                  size="medium"
                />
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      {/* Modals */}
      {editStaff && (
        <EditStaff
          setEditStaff={setEditStaff}
          editStaff={editStaff}
          UpdateInstance={UpdateInstance}
          typeOfStaff={typeOfStaff}
          setTrialsToEdit={setTrialsToEdit}
        />
      )}
      {investigatorModal && (
        <AddInvestigatorModal
          open={investigatorModal}
          onClose={() => setInvestigatorModal(false)}
        />
      )}
    </Box>
  );
};

export default StaffList;
